import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { supabase } from "@/lib/supabase";
import { quotationGuide } from "@/mocks/technical";

interface Notice {
  id: string;
  title: string;
  content: string;
  date: string;
  important: boolean;
}

interface DataRoomItem {
  id: string;
  title: string;
  category: string;
  file_type: string;
  file_size: string;
  date: string;
}

export default function SupportPage() {
  const [activeTab, setActiveTab] = useState<"notices" | "data" | "quotation">("notices");
  const [search, setSearch] = useState("");
  const [expandedNotice, setExpandedNotice] = useState<string | null>(null);

  const [notices, setNotices] = useState<Notice[]>([]);
  const [dataRoom, setDataRoom] = useState<DataRoomItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const fetchNotices = async () => {
    const { data, error } = await supabase
      .from("notices")
      .select("*")
      .order("date", { ascending: false });

    if (!error && data) setNotices(data as Notice[]);
    else if (error) setFetchError(error.message);
  };

  const fetchDataRoom = async () => {
    const { data, error } = await supabase
      .from("data_room")
      .select("*")
      .order("date", { ascending: false });

    if (!error && data) setDataRoom(data as DataRoomItem[]);
    else if (error) setFetchError(error.message);
  };

  useEffect(() => {
    setLoading(true);
    setFetchError("");
    Promise.all([fetchNotices(), fetchDataRoom()]).finally(() => setLoading(false));
  }, []);

  const filteredNotices = notices.filter(
    (n) => !search || n.title.includes(search)
  );
  const filteredData = dataRoom.filter(
    (d) => !search || d.title.includes(search) || d.category.includes(search)
  );

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title="고객지원"
        subtitle="고객과의 소통을 최우선으로 합니다"
        breadcrumb={[{ label: "고객지원" }]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20professional%20customer%20service%20and%20support%20concept%2C%20clean%20organized%20office%20desk%20with%20documents%20and%20communication%20tools%2C%20warm%20natural%20lighting%2C%20professional%20business%20environment%2C%20collaborative%20atmosphere&width=1600&height=680&seq=krubber-support-banner&orientation=landscape"
      />
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-center gap-1 mb-12 bg-background-100 rounded-full p-1 max-w-lg mx-auto">
            {[
              { key: "notices", label: "공지사항" },
              { key: "data", label: "자료실" },
              { key: "quotation", label: "견적문의" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTab(tab.key as typeof activeTab);
                  setSearch("");
                }}
                className={`flex-1 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {fetchError && (
            <div className="mb-6 px-5 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm text-center">
              데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
              <button
                type="button"
                onClick={() => { setFetchError(""); setLoading(true); Promise.all([fetchNotices(), fetchDataRoom()]).finally(() => setLoading(false)); }}
                className="ml-3 underline cursor-pointer font-semibold"
              >
                다시 시도
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20 text-foreground-400">
              <span className="w-5 h-5 flex items-center justify-center animate-spin mr-2">
                <i className="ri-loader-4-line" />
              </span>
              불러오는 중...
            </div>
          ) : (
            <>
              {(activeTab === "notices" || activeTab === "data") && (
                <div className="flex items-center justify-end mb-6">
                  <div className="relative w-full sm:w-[280px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                      <i className="ri-search-line text-sm" />
                    </span>
                    <input
                      type="text"
                      placeholder="검색어 입력"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>
              )}

              {activeTab === "notices" && (
                <div className="flex flex-col">
                  <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-5 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide border-b border-background-200">
                    <span className="col-span-1">번호</span>
                    <span className="col-span-7">제목</span>
                    <span className="col-span-2">작성일</span>
                    <span className="col-span-2">구분</span>
                  </div>
                  {filteredNotices.length === 0 ? (
                    <div className="text-center py-16 text-foreground-400 text-sm">
                      검색 결과가 없습니다.
                    </div>
                  ) : (
                    filteredNotices.map((notice, i) => (
                      <div key={notice.id}>
                        <button
                          type="button"
                          onClick={() => setExpandedNotice(expandedNotice === notice.id ? null : notice.id)}
                          className="w-full grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center px-5 py-4 text-left border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                        >
                          <span className="sm:col-span-1 text-xs text-foreground-500">{filteredNotices.length - i}</span>
                          <span className="sm:col-span-7 text-sm font-medium text-foreground-900 flex items-center gap-2">
                            {notice.important && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold flex-shrink-0">
                                중요
                              </span>
                            )}
                            {notice.title}
                          </span>
                          <span className="sm:col-span-2 text-xs text-foreground-500">{notice.date}</span>
                          <span className="sm:col-span-2 text-xs text-foreground-500 flex items-center gap-1">
                            <i className={`text-[10px] ${expandedNotice === notice.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                            {expandedNotice === notice.id ? "접기" : "보기"}
                          </span>
                        </button>
                        {expandedNotice === notice.id && (
                          <div className="px-5 sm:px-16 py-6 bg-background-100 border-b border-background-200">
                            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                              {notice.content}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "data" && (
                <div className="flex flex-col gap-3">
                  {filteredData.length === 0 ? (
                    <div className="text-center py-16 text-foreground-400 text-sm">
                      검색 결과가 없습니다.
                    </div>
                  ) : (
                    filteredData.map((file) => (
                      <div
                        key={file.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-pdf-2-line text-primary-600" />
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-foreground-900">{file.title}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-1.5">
                              <span className="text-xs text-foreground-500">{file.category}</span>
                              <span className="text-xs text-foreground-400">{file.file_type} · {file.file_size}</span>
                              <span className="text-xs text-foreground-400">{file.date}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ml-14 sm:ml-0"
                        >
                          <i className="ri-download-2-line" />
                          다운로드
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "quotation" && (
                <div>
                  <div className="max-w-3xl mx-auto">
                    <div className="p-8 rounded-2xl bg-background-100 border border-background-200/70">
                      <h3 className="text-xl font-heading font-extrabold text-foreground-950">{quotationGuide.title}</h3>
                      <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{quotationGuide.desc}</p>
                      <div className="mt-6">
                        <p className="text-sm font-bold text-foreground-800 mb-3">견적 의뢰 시 필요 정보</p>
                        <ul className="flex flex-col gap-2">
                          {quotationGuide.requiredInfo.map((info) => (
                            <li key={info} className="flex items-start gap-2 text-sm text-foreground-700">
                              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-checkbox-circle-line text-primary-500" />
                              </span>
                              {info}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="mt-8">
                        <p className="text-sm font-bold text-foreground-800 mb-4">견적 프로세스</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {quotationGuide.process.map((step) => (
                            <div key={step.step} className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                              <span className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center mx-auto text-sm font-extrabold">
                                {step.step}
                              </span>
                              <p className="mt-3 text-sm font-bold text-foreground-900">{step.title}</p>
                              <p className="mt-1 text-xs text-foreground-500">{step.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Link
                        to="/contact"
                        className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-mail-send-line" />
                        견적 문의하기
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}